import * as React from 'react';
import groundImage from '../../../../../images/ground_pattern.svg';
import { getImgSrcWithColor, getTextureByMaterial, loadImage } from '../../../../../utils/graphicEngine';
import { IVerticalDropLongitudinalProps } from './IVerticalDrop';
import textureProtectionSystem from '../../../../../images/texture_protection_system.svg';
import { setDesignImg } from '../../../../store/project/resultsSlice';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { roundToOne, roundToTwo } from '../../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getVerticalDropsType } from '../../../../../utils/utils';
import { EVerticalDropsTypes } from '../../../../enum/general';
import { EConversionSIToImperial, convertTo } from '../../../../../conversion';

//SETUP
const setup = {
    gabionColor: 'grey',
    borderGabionColor: 'black',
    errorColor: 'red',
    anchorColor: '#c6a664',
    waterColor: '#83d7ee',

    borderWidth: 1,

    indicatorHeight: 30,
    indicatorWidth: 55,
    font: '11px Arial',
    colorIndicator: 'red',
    indicatorLineWidth: 1,

    counterweirWidth: 1,
    counterweirUnderSoil: 0.5,
    counterweirTerminalHeight: 0.3,
    counterweirTerminalWidth: 2,

    heightSoil: 2,
};
//

const VerticalDropLongitudinal = (props: IVerticalDropLongitudinalProps) => {
    const verticalDropCanvas = React.useRef<HTMLCanvasElement>(null);
    let ctx: CanvasRenderingContext2D;

    let soilImageSrc: HTMLImageElement;
    let stillingBasinImageSrc: HTMLImageElement | undefined = undefined;
    let baseImageSrc: HTMLImageElement | undefined = undefined;

    let weirGeometry = { ...props.weirGeometry };
    let stillingBasin = { ...props.stillingBasin };
    const { scale, result, inspectAttribute } = props;
    const dispatch = useAppDispatch();

    const verticalDropsType = getVerticalDropsType(stillingBasin);

    let heightGabionFromSoil = 0;

    let wingsGabion = weirGeometry.layer[0];
    let firstGabion = weirGeometry.layer[1];
    let lastGabion = weirGeometry.layer[weirGeometry.layer.length - 1];

    weirGeometry.layer.forEach(x => (heightGabionFromSoil += x.layerHeight));
    heightGabionFromSoil -= wingsGabion.layerHeight;
    heightGabionFromSoil -= weirGeometry.foundation;

    let heightFromSoil = result ? Math.max(result.hydraulicResult.backwaterDepth, wingsGabion.layerHeight) : wingsGabion.layerHeight;

    let scourLocation = 0;
    let scourDepth = 0;

    let widthResult = 0;

    let z0 = 0;
    let zg = 0;
    let zv = 0;
    let z1 = 0;
    let z2 = 0;
    let z3 = 0;

    let counterweirUnderSoil = 0;
    let counterweirWidth = 0;

    const getLayerOverSoil = () => {
        let t = 0;
        return weirGeometry.layer
            .slice()
            .reverse()
            .find(x => {
                t += x.layerHeight;
                return t >= weirGeometry.foundation;
            });
    };

    let layerOverSoil = getLayerOverSoil();

    const getHydraulicParams = () => {
        switch (verticalDropsType) {
            case EVerticalDropsTypes.NotSet:
            case EVerticalDropsTypes.UnlinedVerticalDrop:
                if (!!result) {
                    scourLocation = result.hydraulicResult.scourLocation!;
                    scourDepth = Math.abs(result.hydraulicResult.scourDepth);
                    z0 = result.hydraulicResult.backwaterDepth;
                    zg = result.hydraulicResult.waterDepthOnCrest;
                    zv = result.hydraulicResult.waterCushionElevationAtWeirToe;
                    z1 = result.hydraulicResult.waterDepthOfSupercriticalFlow;
                    z2 = result.hydraulicResult.tailwaterDepthDueToCounterweir;
                    z3 = result.hydraulicResult.waterDepthDownstreamTheStructure;
                }
                counterweirUnderSoil = setup.counterweirUnderSoil;
                counterweirWidth = setup.counterweirWidth;
                break;
            case EVerticalDropsTypes.UnlinedPoolAndCountweir:
                if (!!result) {
                    scourLocation = result.hydraulicResult.locationOfHydraulicJumpStart!;
                    scourDepth = Math.abs(result.hydraulicResult.scourDepth);
                    z0 = result.hydraulicResult.backwaterDepth;
                    zg = result.hydraulicResult.waterDepthOnCrest;
                    zv = result.hydraulicResult.waterCushionElevationAtWeirToe;
                    z1 = result.hydraulicResult.waterDepthOfSupercriticalFlow;
                    z2 = result.hydraulicResult.tailwaterDepthDueToCounterweir;
                    z3 = result.hydraulicResult.waterDepthDownstreamTheStructure;
                }
                counterweirUnderSoil = setup.counterweirUnderSoil;
                counterweirWidth = setup.counterweirWidth;
                break;
            case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
                if (!!result) {
                    scourLocation = result.hydraulicResult.locationOfHydraulicJumpStart!;
                    scourDepth = Math.abs(result.hydraulicResult.scourDepth);
                    z0 = result.hydraulicResult.backwaterDepth;
                    zg = result.hydraulicResult.waterDepthOnCrest;
                    zv = result.hydraulicResult.waterCushionElevationAtWeirToe;
                    z1 = result.hydraulicResult.waterDepthOfSupercriticalFlow;
                    z2 = result.hydraulicResult.tailwaterDepthDueToCounterweir;
                    z3 = result.hydraulicResult.waterDepthDownstreamTheStructure;
                }
                counterweirUnderSoil = setup.counterweirUnderSoil;
                counterweirWidth = setup.counterweirWidth;
                break;
            case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
                if (!!result) {
                    scourLocation = result.hydraulicResult.locationOfHydraulicJumpStart!;
                    scourDepth = Math.abs(stillingBasin.stillingBasinElevation!);
                    z0 = result.hydraulicResult.backwaterDepth;
                    zg = result.hydraulicResult.waterDepthOnCrest;
                    zv = result.hydraulicResult.waterCushionElevationAtWeirToe;
                    z1 = result.hydraulicResult.waterDepthOfSupercriticalFlow;
                    z2 = result.hydraulicResult.tailwaterDepthDueToCounterweir;
                    z3 = result.hydraulicResult.waterDepthDownstreamTheStructure;
                }
                counterweirUnderSoil = Math.abs(stillingBasin.stillingBasinElevation!) + stillingBasin.thickness!;
                counterweirWidth = Math.abs(stillingBasin.stillingBasinElevation!) + stillingBasin.thickness!;
                break;
        }
    };

    getHydraulicParams();

    const getResultWidth = () => {
        if (!!result) {
            switch (verticalDropsType) {
                case EVerticalDropsTypes.NotSet:
                case EVerticalDropsTypes.UnlinedVerticalDrop:
                    widthResult = result.hydraulicResult.scourLocation + 5;
                    break;
                case EVerticalDropsTypes.UnlinedPoolAndCountweir:
                case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
                case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
                    let stillingBasinWidth = stillingBasin.poolLength! + counterweirWidth + setup.counterweirTerminalWidth;
                    let waterWidth = scourLocation + result!.hydraulicResult.jumpLengthUsace1990 + 0.5;
                    widthResult = stillingBasinWidth > waterWidth ? 0 : waterWidth - stillingBasinWidth;
                    break;
            }
        }
    };

    getResultWidth();

    let _height = heightFromSoil + heightGabionFromSoil + weirGeometry.foundation + setup.heightSoil + setup.indicatorHeight / scale;
    let _width =
        setup.indicatorWidth / scale +
        setup.borderWidth +
        heightGabionFromSoil +
        lastGabion.layerWidth +
        (stillingBasin.isCounterweir ? stillingBasin.poolLength! + counterweirWidth + setup.counterweirTerminalWidth : 0) +
        widthResult +
        setup.borderWidth +
        setup.indicatorWidth / scale;

    inspectAttribute(_width, _height);

    React.useEffect(() => {
        let verticalDropCanvasEle = verticalDropCanvas.current!;
        ctx = verticalDropCanvasEle.getContext('2d')!;
        Promise.all([loadStillingBasinImage(), loadSoilImage(), loadBaseImage()]).then(() => {
            cleanCanvas();
            if (!!result) {
                createWaterSection();
            }
            createSoil();
            createWeirGeometry();
            createStillingBasin();
            createIndicator();
            dispatch(setDesignImg(verticalDropCanvasEle.toDataURL('image/png')));
        });
    }, [props]);

    const createSoilSectionUnlinedVerticalDropOrUnlinedPoolAndCountweir = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        ctx.fillStyle = pat;
        if (!!result) {
            let offsetForBugFix = 1; // without it show a white line;
            ctx.fillRect(
                (scale * setup.indicatorWidth) / scale,
                scale * (heightFromSoil + heightGabionFromSoil),
                scale * (setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth) + offsetForBugFix,
                scale * (weirGeometry.foundation + setup.heightSoil),
            );
            ctx.closePath();

            ctx.beginPath();
            ctx.moveTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth), scale * (heightFromSoil + heightGabionFromSoil));
            ctx.quadraticCurveTo(
                scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation),
                scale * (heightFromSoil! + heightGabionFromSoil + 2 * scourDepth),
                scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + 5),
                scale * (heightFromSoil + heightGabionFromSoil),
            );
            ctx.lineTo(
                scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + 5),
                scale * (_height - setup.indicatorHeight / scale),
            );
            ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth), scale * (_height - setup.indicatorHeight / scale));
            ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth), scale * (heightFromSoil + heightGabionFromSoil));
            ctx.fill();
            ctx.closePath();

            ctx.beginPath();
            ctx.moveTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + 5), scale * (heightFromSoil + heightGabionFromSoil));
            ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (heightFromSoil + heightGabionFromSoil));
            ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (_height - setup.indicatorHeight / scale));
            ctx.lineTo(
                scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + 5),
                scale * (_height - setup.indicatorHeight / scale),
            );
            ctx.fill();
            ctx.closePath();
        } else {
            ctx.fillRect(
                (scale * setup.indicatorWidth) / scale,
                scale * (heightFromSoil + heightGabionFromSoil),
                scale * (_width - (2 * setup.indicatorWidth) / scale),
                scale * (weirGeometry.foundation + setup.heightSoil),
            );
        }
        ctx.closePath();
    };

    const createSoilSectionLinedPoolAndCountweirWithoutScoutDepth = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        ctx.fillStyle = pat;
        ctx.fillRect(
            (scale * setup.indicatorWidth) / scale,
            scale * (heightFromSoil + heightGabionFromSoil),
            scale * (_width - (2 * setup.indicatorWidth) / scale),
            scale * (weirGeometry.foundation + setup.heightSoil),
        );
        ctx.closePath();
    };
    const createSoilSectionLinedPoolAndCountweirWithNegativeScoutDepth = () => {
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        ctx.fillStyle = pat;
        let offsetForBugFix = 1; // without it show a white line;
        ctx.fillRect(
            (scale * setup.indicatorWidth) / scale,
            scale * (heightFromSoil + heightGabionFromSoil),
            scale * (setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - layerOverSoil!.layerOffset),
            scale * (weirGeometry.foundation + setup.heightSoil),
        );
        ctx.fillRect(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - layerOverSoil!.layerOffset) - offsetForBugFix,
            scale * (heightFromSoil + heightGabionFromSoil + Math.abs(stillingBasin.stillingBasinElevation!)),
            scale * stillingBasin.poolLength! + 2 * offsetForBugFix,
            scale * (weirGeometry.foundation + setup.heightSoil - Math.abs(stillingBasin.stillingBasinElevation!)),
        );

        ctx.beginPath();
        ctx.moveTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - layerOverSoil!.layerOffset + stillingBasin.poolLength!),
            scale * (heightFromSoil + heightGabionFromSoil),
        );
        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (heightFromSoil + heightGabionFromSoil));
        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - layerOverSoil!.layerOffset + stillingBasin.poolLength!),
            scale * (_height - setup.indicatorHeight / scale),
        );
        ctx.fill();
        ctx.closePath();

        ctx.closePath();
    };

    const createWaterSectionUnlinedVerticalDrop = () => {
        ctx.beginPath();
        ctx.lineJoin = 'round';
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth / scale, scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - firstGabion.layerOffset), scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        //water cushion elevation
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil - zv),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation / 2 - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil - zv),
        );
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * heightFromSoil!);
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z1),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + 4.5),
            scale * (heightFromSoil! + heightGabionFromSoil - z3),
        );

        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (heightFromSoil! + heightGabionFromSoil - z3));
        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (heightFromSoil! - z0));
        ctx.fill();
        ctx.closePath();
    };

    const createWaterSectionUnlinedPoolAndCountweir = () => {
        ctx.beginPath();
        ctx.lineJoin = 'round';
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth / scale, scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - firstGabion.layerOffset), scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        //water cushion elevation
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil - zv),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation / 2 - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil - zv),
        );
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * heightFromSoil!);
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z1),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + (result!.hydraulicResult.jumpLengthUsace1990 * 2) / 3),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + result!.hydraulicResult.jumpLengthUsace1990),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + result!.hydraulicResult.jumpLengthUsace1990 + 0.5),
            scale * (heightFromSoil! + heightGabionFromSoil - z3),
        );

        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (heightFromSoil! + heightGabionFromSoil - z3));
        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (heightFromSoil! - z0));
        ctx.fill();
        ctx.closePath();
    };
    const createWaterSectionLinedPoolAndCountweirWithoutScoutDepth = () => {
        ctx.beginPath();
        ctx.lineJoin = 'round';
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth / scale, scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - firstGabion.layerOffset), scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        //water cushion elevation
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - zv),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation / 2 - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - zv),
        );
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * heightFromSoil!);
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z1),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + (result!.hydraulicResult.jumpLengthUsace1990 * 2) / 3),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + result!.hydraulicResult.jumpLengthUsace1990),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + result!.hydraulicResult.jumpLengthUsace1990 + 0.5),
            scale * (heightFromSoil! + heightGabionFromSoil - z3),
        );

        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (heightFromSoil! + heightGabionFromSoil - z3));
        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (heightFromSoil! - z0));
        ctx.fill();
        ctx.closePath();
    };
    const createWaterSectionLinedPoolAndCountweirWithNegativeScoutDepth = () => {
        ctx.beginPath();
        ctx.lineJoin = 'round';
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth / scale, scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - firstGabion.layerOffset), scale * (heightFromSoil! - z0));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        //water cushion elevation
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil - zv),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation / 2 - firstGabion.layerOffset),
            scale * (heightFromSoil! + heightGabionFromSoil - zv),
        );
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * heightFromSoil!);
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerOffset), scale * (heightFromSoil! - zg));

        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z1),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + (result!.hydraulicResult.jumpLengthUsace1990 * 2) / 3),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + result!.hydraulicResult.jumpLengthUsace1990),
            scale * (heightFromSoil! + heightGabionFromSoil + scourDepth - z2),
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + scourLocation + result!.hydraulicResult.jumpLengthUsace1990 + 0.5),
            scale * (heightFromSoil! + heightGabionFromSoil - z3),
        );

        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (heightFromSoil! + heightGabionFromSoil - z3));
        ctx.lineTo(scale * (_width - setup.indicatorWidth / scale), scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (_height - setup.indicatorHeight / scale));
        ctx.lineTo((scale * setup.indicatorWidth) / scale, scale * (heightFromSoil! - z0));
        ctx.fill();
        ctx.closePath();
    };

    const createWaterSection = () => {
        switch (verticalDropsType) {
            case EVerticalDropsTypes.NotSet:
            case EVerticalDropsTypes.UnlinedVerticalDrop:
                createWaterSectionUnlinedVerticalDrop();
                break;
            case EVerticalDropsTypes.UnlinedPoolAndCountweir:
                createWaterSectionUnlinedPoolAndCountweir();
                break;
            case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
                createWaterSectionLinedPoolAndCountweirWithoutScoutDepth();
                break;
            case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
                createWaterSectionLinedPoolAndCountweirWithNegativeScoutDepth();
                break;
        }
    };

    const cleanCanvas = () => {
        let canvasEle = verticalDropCanvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const loadStillingBasinImage = async (): Promise<void> => {
        let texture = getTextureByMaterial(stillingBasin.material!.type, stillingBasin.material!.category);
        if (!!texture) {
            let imgSrcWithColor = getImgSrcWithColor(texture, stillingBasin.material.color);
            stillingBasinImageSrc = await loadImage(imgSrcWithColor);
        }
    };

    const loadBaseImage = async (): Promise<void> => {
        let imgSrcWithColor = getImgSrcWithColor(textureProtectionSystem, setup.gabionColor);
        baseImageSrc = await loadImage(imgSrcWithColor);
    };

    const loadSoilImage = async (): Promise<void> => {
        soilImageSrc = await loadImage(groundImage);
    };

    const createSoil = () => {
        switch (verticalDropsType) {
            case EVerticalDropsTypes.NotSet:
            case EVerticalDropsTypes.UnlinedVerticalDrop:
            case EVerticalDropsTypes.UnlinedPoolAndCountweir:
                createSoilSectionUnlinedVerticalDropOrUnlinedPoolAndCountweir();
                break;
            case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
                createSoilSectionLinedPoolAndCountweirWithoutScoutDepth();
                break;
            case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
                createSoilSectionLinedPoolAndCountweirWithNegativeScoutDepth();
                break;
        }
    };

    const createIndicator = () => {
        //wings height
        createIndicatorY(heightFromSoil - wingsGabion.layerHeight, heightFromSoil, setup.indicatorWidth / scale / 2, wingsGabion.layerHeight);
        //weirGeometry height
        let layerHeight = 0;
        weirGeometry.layer.forEach(x => {
            if (x.layerNumber !== 1) {
                layerHeight += x.layerHeight;
            }
        });
        createIndicatorY(heightFromSoil, heightFromSoil + layerHeight - weirGeometry.foundation, setup.indicatorWidth / scale / 2, layerHeight - weirGeometry.foundation);
        //weirGeometry foundation height
        createIndicatorY(heightFromSoil + layerHeight - weirGeometry.foundation, heightFromSoil + layerHeight, setup.indicatorWidth / scale / 2, weirGeometry.foundation);
        //weirGeometry gabion width
        createIndicatorX(
            setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset - layerOverSoil!.layerWidth,
            setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset,
            _height - setup.indicatorHeight / scale / 2,
            layerOverSoil!.layerWidth,
        );
        //stilling basin length
        if (stillingBasin.isCounterweir) {
            createIndicatorX(
                setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset,
                setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset + stillingBasin.poolLength!,
                _height - setup.indicatorHeight / scale / 2,
                stillingBasin.poolLength!,
            );
            createIndicatorX(
                setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset + stillingBasin.poolLength!,
                setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset + stillingBasin.poolLength! + counterweirWidth,
                _height - setup.indicatorHeight / scale / 2,
                counterweirWidth!,
            );
            createIndicatorX(
                setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion!.layerWidth - layerOverSoil!.layerOffset + stillingBasin.poolLength! + counterweirWidth,
                setup.indicatorWidth / scale +
                    setup.borderWidth +
                    heightGabionFromSoil +
                    lastGabion!.layerWidth -
                    layerOverSoil!.layerOffset +
                    stillingBasin.poolLength! +
                    counterweirWidth +
                    setup.counterweirTerminalWidth,
                _height - setup.indicatorHeight / scale / 2,
                setup.counterweirTerminalWidth,
            );
            createIndicatorY(
                heightFromSoil + heightGabionFromSoil - stillingBasin.counterweirElevation!,
                heightFromSoil + heightGabionFromSoil,
                _width - setup.indicatorWidth / scale / 2,
                stillingBasin.counterweirElevation!,
            );
            createIndicatorY(
                heightFromSoil + heightGabionFromSoil,
                heightFromSoil + heightGabionFromSoil + setup.counterweirUnderSoil,
                _width - setup.indicatorWidth / scale / 2,
                setup.counterweirUnderSoil!,
            );
        }
    };

    const createIndicatorY = (moveToY: number, lineToY: number, x: number, value: number) => {
        createLine([], scale * x - 2, scale * moveToY, scale * x + 2, scale * moveToY);
        createLine([], scale * x, scale * moveToY, scale * x, scale * lineToY, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], scale * x - 2, scale * lineToY, scale * x + 2, scale * lineToY);
    };

    const createIndicatorX = (moveToX: number, lineToX: number, y: number, value: number) => {
        createLine([], scale * moveToX, scale * y - 2, scale * moveToX, scale * y + 2);
        createLine([], scale * moveToX, scale * y, scale * lineToX, scale * y, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], scale * lineToX, scale * y - 2, scale * lineToX, scale * y + 2);
    };

    const createLine = (segments: number[], moveToX: number, moveToY: number, lineToX: number, lineToY: number, value?: number) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = setup.colorIndicator!;
        ctx.font = setup.font;
        ctx.lineWidth = setup.indicatorLineWidth;
        ctx.lineCap = 'round';
        if (value && value > 0) {
            ctx.fillStyle = 'black';
            ctx.fillText(value.toString(), (moveToX + lineToX) / 2 + 2, (moveToY + lineToY) / 2 - 2);
        }
        ctx.stroke();
    };

    const handleGabionOverHeight = (x: number, y: number, w: number, h: number, colorGabion?: string | CanvasPattern | CanvasGradient) => {
        if (h > 1) {
            for (let i = 1; i <= h; i++) {
                colorGabion ? createRectWithBorder(scale * x, scale * (y + h - i), scale * w, scale * 1, colorGabion) : ctx.strokeRect(scale * x, scale * (y + h - i), scale * w, scale * 1);
            }
            if (h - Math.trunc(h) > 0) {
                colorGabion
                    ? createRectWithBorder(scale * x, scale * y, scale * w, scale * (h - Math.trunc(h)), colorGabion)
                    : ctx.strokeRect(scale * x, scale * y, scale * w, scale * (h - Math.trunc(h)));
            }
        } else {
            colorGabion ? createRectWithBorder(scale * x, scale * y, scale * w, scale * h, colorGabion) : ctx.strokeRect(scale * x, scale * y, scale * w, scale * h);
        }
    };

    const createWeirGeometry = () => {
        //anchor
        ctx.beginPath();
        ctx.moveTo(scale * (setup.indicatorWidth / scale + setup.borderWidth), scale * (heightFromSoil + heightGabionFromSoil));
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil), scale * heightFromSoil);
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerWidth - firstGabion.layerOffset),
            scale * heightFromSoil,
        );
        ctx.lineTo(
            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - firstGabion.layerWidth - firstGabion.layerOffset),
            scale * (heightFromSoil + heightGabionFromSoil),
        );
        ctx.lineTo(scale * (setup.indicatorWidth / scale + setup.borderWidth), scale * (heightFromSoil + heightGabionFromSoil));
        ctx.fillStyle = setup.anchorColor;
        ctx.fill();
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.stroke();
        ctx.closePath();

        let isThereStability_Macra2_Vertical = result?.staticResult?.stabilityChecksOutput?.isStabilityError;

        let basePat = ctx.createPattern(baseImageSrc!, 'repeat');
        let layerHeight = 0;
        weirGeometry.layer
            .slice()
            .reverse()
            .forEach(x => {
                layerHeight += x.layerHeight;
                if (x.layerNumber == 1) {
                    //wings
                    ctx.save();
                    ctx.setLineDash([6]);
                    if (x.layerHeight === 2) {
                        ctx.strokeRect(
                            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 2 - x.layerOffset),
                            scale * (heightFromSoil - 1),
                            scale * 2,
                            scale * 1,
                        );
                        ctx.strokeRect(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - x.layerOffset), 0, scale * 1, scale * 1);
                    } else if (x.layerHeight === 2.5) {
                        ctx.strokeRect(
                            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 2 - x.layerOffset),
                            scale * (heightFromSoil - 1),
                            scale * 2,
                            scale * 1,
                        );
                        handleGabionOverHeight(setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - x.layerOffset, 0, 1, 1.5);
                    } else if (x.layerHeight === 3) {
                        ctx.strokeRect(
                            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 3 - x.layerOffset),
                            scale * (heightFromSoil - 1),
                            scale * 3,
                            scale * 1,
                        );
                        ctx.strokeRect(
                            scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 2 - x.layerOffset),
                            scale * (heightFromSoil - 2),
                            scale * 2,
                            scale * 1,
                        );
                        ctx.strokeRect(scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - 1 - x.layerOffset), 0, scale * 1, scale * 1);
                    } else {
                        handleGabionOverHeight(
                            setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - x.layerWidth - x.layerOffset,
                            heightFromSoil - x.layerHeight,
                            x.layerWidth,
                            x.layerHeight,
                        );
                    }
                    ctx.restore();
                } else {
                    //gabions
                    createRectWithBorder(
                        scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - x.layerWidth - x.layerOffset),
                        scale * (heightFromSoil + heightGabionFromSoil + weirGeometry.foundation - layerHeight),
                        scale * x.layerWidth,
                        scale * x.layerHeight,
                        isThereStability_Macra2_Vertical ? setup.errorColor : basePat!,
                    );
                }
            });
    };

    const createRectWithBorder = (x: number, y: number, w: number, h: number, pat: string | CanvasPattern | CanvasGradient) => {
        ctx.fillStyle = pat;
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.strokeRect(x, y, w, h);
    };

    const createStillingBasin = () => {
        if (stillingBasin.isCounterweir) {
            let isThereCounterweirElevationError_Macra2_Vertical =
                verticalDropsType !== EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth && result?.hydraulicResult?.minimumCounterweirElevation! > stillingBasin.counterweirElevation!;
            let basePat = ctx.createPattern(baseImageSrc!, 'repeat');
            handleGabionOverHeight(
                setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + stillingBasin.poolLength! - layerOverSoil!.layerOffset,
                heightFromSoil + heightGabionFromSoil - stillingBasin.counterweirElevation!,
                counterweirWidth,
                stillingBasin.counterweirElevation! + counterweirUnderSoil,
                isThereCounterweirElevationError_Macra2_Vertical ? setup.errorColor : basePat!,
            );

            //counterweir terminal
            createRectWithBorder(
                scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth + stillingBasin.poolLength! + counterweirWidth - layerOverSoil!.layerOffset),
                scale * (heightFromSoil + heightGabionFromSoil),
                scale * setup.counterweirTerminalWidth,
                scale * setup.counterweirTerminalHeight,
                basePat!,
            );
        }

        if (stillingBasin.isStillingBasin) {
            let pat = !!stillingBasinImageSrc ? ctx.createPattern(stillingBasinImageSrc, 'repeat') : stillingBasin.material.color;
            let isThereStillingBasinWidthError_Macra2_Vertical = stillingBasin?.poolLength! < roundToTwo(result?.hydraulicResult?.minimumBasinLengthUsace1990);
            createRectWithBorder(
                scale * (setup.indicatorWidth / scale + setup.borderWidth + heightGabionFromSoil + lastGabion.layerWidth - layerOverSoil!.layerOffset),
                scale * (heightFromSoil + heightGabionFromSoil + Math.abs(stillingBasin.stillingBasinElevation!)),
                scale * stillingBasin.poolLength!,
                scale * stillingBasin.thickness!,
                isThereStillingBasinWidthError_Macra2_Vertical ? setup.errorColor : pat!,
            );
        }
    };

    return (
        <div className='position-absolute'>
            <canvas width={_width * scale} height={_height * scale} ref={verticalDropCanvas}></canvas>
        </div>
    );
};

export default VerticalDropLongitudinal;
